<template>
    <div class="pr f fv / el-frm-files-wrapper"
        :class="[
            disabled ? 'el--disabled' : '',
            atmax ? 'el--maximum' : '',
        ]">
        <span class="el-frm-lbl sm-b-xs" v-if="lbl">
            <template v-if="!lblEntity">
                {{ lbl | athT }}
            </template>
            <template v-if="lblEntity">
                {{ lbl | athTEnt(lblEntity, lblEntityLc) }}
            </template>
        </span>
        <div class="f fv / el-frm-files">
            <div class="f fh fa-c / el-frm-files-selector-wrapper">
                <label class="el-frm-files-selector">
                    <input class="u-hide"
                        type="file"
                        @change="update"
                        :disabled="disabled || atmax"
                        :multiple="maxFiles > 1"
                        :accept="accept"
                        ref="input" />
                    <div class="el-btn-icon el--s-m"
                        :class="[disabled || atmax ? 'el--disabled' : '']">
                        <i class="lnr-paperclip" aria-hidden="true"></i>
                    </div>
                </label>
                <span class="fg"></span>
                <span v-if="is_over_total" class="el-badge el--t-danger te-be">
                    {{ 'mead.files.over_total' | athT }}
                </span>
            </div>
            <div class="el-frm-files-list sp-xs sm-b-s" v-if="data && data.length > 0">
                <div class="pr f fh fa-c dw-10 oh / el-frm-files-el"
                    v-for="(obj, index) in data"
                    :key="index"
                    :title="obj.name"
                    :class="[
                        (maxFiles > 1 && index < maxFiles && data.length > maxFiles) ? 'el--maxallowed' : '',
                        (maxFiles > 0 && index >= maxFiles && data.length > maxFiles) ? 'el--maxdisallowed' : '',
                    ]">
                    <i class="fns" :class="[obj.icon]" aria-hidden="true"></i>
                    <p class="fg f fv o-ol">
                        <strong class="o-ol">{{ obj.name }}</strong>
                        <small class="te-subnote">{{ obj.meta.human }}</small>
                    </p>
                    <!-- Invalidity -->
                    <span class="el-frm-files-el-invalid fns sm-l-xs" v-if="obj.is_invalid" :title="obj.is_invalid">!</span>
                    <!-- Delete button -->
                    <ButtonIcon class="el--s-xs fns el--transparent sm-l-xs"
                        icon="cross"
                        @click="remove(index)" />
                </div>
            </div>
        </div>
        <span class="te-subtext" v-if="uiSubnote">{{ subnote }}</span>
    </div>
</template>

<script>
import { guuid, humanizeBytes, isArray, isObject } from '@aretheyhappy/utils';
import ButtonIcon from '../ButtonIcon.vue';

//  Retrieve an icon based on the file type extension
const getIcon = ext => {
    switch (ext) {
        case 'txt':
            return 'lnr-document';
        case 'jpg':
        case 'jpeg':
        case 'png':
            return 'lnr-picture';
        case 'mov':
        case 'avi':
        case 'mp4':
            return 'lnr-play';
        case 'vcf':
            return 'lnr-profile';
        case 'xls':
        case 'xlsx':
            return 'lnr-file-spreadsheet';
        case 'zip':
        case 'rar':
        case '7z':
            return 'lnr-file-zip';
        default:
            return 'lnr-file-empty';
    }
};

export default {
    name: 'mead-form-files',
    components: {ButtonIcon},
    data () {
        return {
            data: [],
            is_over_total: false,
        };
    },
    props: {
        /**
         * Array of currently attached files (v-model).
         */
        value           : {type: [Boolean, Array], default: () => false},
        /**
         * Disables file selection and removal.
         */
        disabled        : {type: Boolean, default: false},
        /**
         * Comma separated list of accepted mime types, applied to the file input and used for validation.
         */
        accept          : {type: [Boolean, String], default: false},
        /**
         * Label i18n key rendered above the control. Only shown when truthy.
         */
        lbl             : {type: [String, Boolean], default: () => false},
        /**
         * Entity name interpolated into the label when translating it via `athTEnt`.
         */
        lblEntity       : {type: [Boolean, String], default: () => false},
        /**
         * Whether the interpolated entity name should be lowercased in the translated label.
         */
        lblEntityLc     : {type: Boolean, default: () => true},
        /**
         * Maximum number of files allowed.
         */
        max             : {type: [Number, Array], default: () => 10},
        /**
         * Maximum file size, in bytes, allowed per file. Defaults to 10 Megabytes (10485760).
         */
        maxBytes        : {type: [Boolean, Number], default: () => 10485760},
        /**
         * Maximum combined size, in bytes, allowed across all attached files.
         */
        maxTotalBytes   : {type: [Boolean, Number], default: () => false},
        /**
         * Minimum file size, in bytes, required per file.
         */
        minBytes        : {type: [Boolean, Number], default: () => false},
        /**
         * Whether to show the subnote describing the current file count/type/size constraints.
         */
        uiSubnote       : {type: Boolean, default: () => true},
    },
    methods: {
        init: async function () {
            this.data = this.maxFiles
                ? [...this.value || []].splice(0, this.maxFiles)
                : [...this.value || []];

            if (isArray(this.data) && this.data.length > 0) {
                this.notify();
            }
        },
        open: function () {
            this.$refs.clicker.click();
        },
        //  Send out input emission
        notify: function () {
            //  Revalidate
            for (const el of this.data) {
                if (!isObject(el)) continue;
                el.is_invalid = this.validateObject(el);
            }

            const files = [...this.data]
                .splice(0, this.maxFiles)
                .map(el => Object.assign({}, el))
                .filter(el => el.is_invalid === false);

            const total = files.reduce((acc, el) => acc + el.meta.bytes, 0);

            //  Only emit up to max and filter out the invalid ones
            this.$emit('input', files);

            //  Set over total
            this.is_over_total = this.maxTotalBytes && total > this.maxTotalBytes;
            this.$emit('isValid', !this.is_over_total);
        },
        update: function (evt) {
            //  Take max 10 files in a single selection, if more are selected cut those off.
            //  This is for performance reasons
            const value = [].slice.call(evt.target.files || []).splice(0, 10);

            //  Clear out file input
            this.$refs.input.value = '';

            //  Serialize selected files
            const acc = [];
            for (const el of value) {
                const {size, type, lastModified} = el;
                const name = el.name.split('.');
                const extension = name.pop();

                //  Create object and push into accumulator
                acc.push({
                    is_invalid  : false,
                    uid         : guuid(),
                    name        : [...name, extension].join('.'),
                    ext         : extension,
                    mime_type   : type,
                    type        : type.split('/')[0],
                    file        : el,
                    icon        : getIcon(extension),
                    meta        : {
                        bytes       : size,
                        human       : humanizeBytes(size),
                        updated_at  : lastModified,
                        name        : el.name,  //  Original name, used for storage
                    },
                });
            }

            //  Concat new accumulator into data and filter out anything that is seen already
            //  uniqueness checks here are a combination of name, size, modified and extension
            const hash_map = new Map();
            let hash = false;
            this.data = this.data.concat(acc).filter(el => {
                hash = `${el.name}${el.meta.bytes}${el.meta.updated_at}${el.ext}`;
                if (hash_map.has(hash)) return false;
                hash_map.set(hash, true);
                return el;
            });

            //  Set validity
            for (const el of this.data) el.is_invalid = this.validateObject(el);

            this.notify();
        },
        remove: function (index) {
            this.data.splice(index, 1);
            this.notify();
        },
        //  Validate a form media object
        validateObject: function (obj) {
            const {bytes = 0} = (obj || {}).meta || {};
            const {mime_type = false} = obj;
            let msg = [];

            //  Validate
            msg.push(this.validateSet(bytes, this.minBytes, this.maxBytes, 'bytes'));

            if ((this.accept || []).length > 0 && !this.accept.includes(mime_type)) {
                msg.push(MeadCommon.I18N.go(
                    'mead.files.types',
                    {lst: this.accept.split(',').map(el => el.split('/')[1]).join(', ')}
                ));
            }

            //  Serialize
            msg = msg.filter(el => el !== false);

            return msg.length > 0 ? msg.join('\n') : false;
        },
        validateSet: function (val, min, max, base) {
            if (max !== false && val > max) {
                return MeadCommon.I18N.go(
                    `mead.files.${base}_max_tooltip`,
                    {amt: base === 'bytes' ? humanizeBytes(max) : max}
                );
            }
            if (min !== false && val < min) {
                return MeadCommon.I18N.go(
                    `mead.files.${base}_min_tooltip`,
                    {amt: base === 'bytes' ? humanizeBytes(min) : min}
                );
            }
            return false;
        },
        getSubnoteMsg: function (min = false, max = false, base = false, use_bytes = false) {
            if (min !== false && max !== false) {
                return MeadCommon.I18N.go(`mead.files.${base}`, {
                    max: use_bytes ? humanizeBytes(max) : max,
                    min: use_bytes ? humanizeBytes(min) : min,
                });
            }

            //  Max bytes (if only this)
            if (min === false && max !== false) {
                return MeadCommon.I18N.go(`mead.files.${base}_max`, {amt: use_bytes ? humanizeBytes(max) : max});
            }

            //  Min bytes (if only this)
            if (min !== false && max === false) {
                return MeadCommon.I18N.go(`mead.files.${base}_min`, {amt: use_bytes ? humanizeBytes(min) : min});
            }

            return false;
        },
    },
    computed: {
        maxFiles: function () {
            return this.max;
        },
        atmax: function () {
            if (!this.data) return false;
            return this.data.length >= this.maxFiles;
        },
        subnote: function () {
            return [
                //  Max files
                MeadCommon.I18N.go('mead.files.max', {amt: this.maxFiles}),
                //  File types
                this.accept
                    ? MeadCommon.I18N.go('mead.files.types', {lst: this.accept.split(',').map(el => el.split('/')[1]).join(', ')})
                    : false,
                //  Bytes
                this.getSubnoteMsg(this.minBytes, this.maxBytes, 'bytes', true),
                this.getSubnoteMsg(false, this.maxTotalBytes, 'bytes_total', true),
            ].filter(el => el !== false).join(' | ');
        },
    },
    created () {
        this.init();
    },
};
</script>

<style lang="scss" scoped>
@use '../../../../sass/variables' as *;

.el-frm-files-selector-wrapper {
    margin-bottom: $s-s;
}

.el-frm-files-selector .el-btn-icon {
    font-size: $fs-l;
    border-radius: $br-m;
    height: 4rem;
    width: 4rem;
    line-height: 4rem;

    &.el--disabled {
        cursor: not-allowed;
        opacity: .5;
    }
}

.el-frm-files-list {
    background-color: var(--c-brand-light-bg);
    color: var(--c-brand-light-fg);
    max-height: 15rem;
    border-radius: 1rem;
    overflow-y: auto;
}

.el-frm-files-el {
    padding: $s-xs 0;

    &:not(:last-of-type) {
        border-bottom: 1px dotted var(--c-panel-border);
    }

    i {
        font-size: $fs-m;
        margin: 0 $s-s 0 $s-xs;
    }

    strong {
        font-size: $fs-s;
    }

    div {
        background-repeat: no-repeat;
        background-size: cover;
        background-position: center center;
        width: 100%;
        height: 100%;
        border-radius: $br-m;
    }

    .el-frm-files-el-invalid {
        border-radius: 50%;
        font-size: $fs-s;
        width: $fs-xl;
        height: $fs-xl;
        background-color: var(--c-brand-danger-bg);
        color: var(--c-brand-danger-fg);
        text-align: center;
        font-weight: bold;
        line-height: $fs-xl;
    }

    //  MODIFIERS

    &.el--maxdisallowed {
        & > i,
        & > p {
            opacity: .2;
        }
    }
}
</style>
